import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext, API, APP_NAME, APP_URL } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const ServiceSheetView = () => {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const { token, shop } = useContext(AuthContext);
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    fetchIntake();
  }, [intakeId]);

  // Update page title
  useEffect(() => {
    document.title = `Service Sheet - ${APP_NAME}`;
  }, []);

  const fetchIntake = async () => {
    try {
      const response = await axios.get(`${API}/shop/intake/${intakeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntake(response.data);
    } catch (error) {
      toast.error("Failed to load intake");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const parseSummary = (summary) => {
    if (!summary) return {};

    const lines = summary.split("\n").map((l) => l.trim());

    // Now parse sections (same logic as before)
    const sections = [];
    let currentTitle = null;
    let currentContent = [];

    const isHeading = (line) => /^[A-Z0-9 &]+:$/.test(line);

    lines.forEach((line) => {
      if (isHeading(line)) {
        // Save previous
        if (currentTitle) {
          sections.push({
            title: currentTitle,
            value: currentContent.join("\n").trim(),
          });
        }

        currentTitle = line.replace(":", "").trim();
        currentContent = [];
      } else {
        if (currentTitle && line !== "") {
          currentContent.push(line);
        }
      }
    });

    // Push last section
    if (currentTitle) {
      sections.push({
        title: currentTitle,
        value: currentContent.join("\n").trim(),
      });
    }

    return {
      sections
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return <div className="text-[#f8f8f8]">Loading service sheet...</div>;
  }

  if (!intake) {
    return <div className="text-[#f8f8f8]">Service sheet not found</div>;
  }

  const parsed = parseSummary(intake.ai_summary);
  console.log(parsed)

  return (
    <div className="max-w-5xl mx-auto">
      {/* Print/Back buttons - hide when printing */}
      <div className="mb-6 flex gap-3 justify-between print:hidden">
        <Button
          data-testid="back-to-intakes-btn"
          onClick={() => navigate("/dashboard/intakes")}
          variant="outline"
          className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back <span className="hidden md:inline-block">to Service Sheets</span>
        </Button>
        <Button
          data-testid="print-pull-sheet-btn"
          onClick={handlePrint}
          className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print <span className="hidden md:inline-block">Service Sheet</span>
        </Button>
      </div>

      {/* Service Sheet - optimized for print */}
      <div className="bg-white text-[#0a1a1f] rounded-lg p-8 print:p-0 print:rounded-none">
        {/* Header */}
        <div className="border-b-2 border-[#005f6b] pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {shop?.logo_url && (
                <img
                  src={shop.logo_url}
                  alt={shop.shop_name}
                  className="h-16 w-auto mb-4"
                />
              )}
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-3xl font-bold text-[#005f6b]">
                  {shop?.shop_name || "Business"}
                </h1>
              </div>
              <p className="text-sm text-gray-600">Client Service Sheet</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm font-medium">{formatDate(intake.created_at)}</p>
              <div className="mt-2 text-xs px-3 py-1 rounded-full border bg-[#00ced1]/20 text-[#00ced1] border-[#00ced1]/30">{intake.service_type?.name}</div>

            </div>
          </div>

          {/* Powered by BackOffice */}
          <div className="flex items-center justify-end mt-2 hidden print:flex">
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3"
            >
              <div className="bg-white px-2 py-1 rounded-md flex items-center gap-2 shadow-sm">
                <img src="https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp" alt="BackOffice Logo" className="w-8" />
                <div className="text-black text-xs">Powered by BackOffice</div>
              </div>
            </a>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6 bg-[#f8f8f8] p-4 rounded">
            <div>
              <p className="text-xs text-gray-500 uppercase">Client Name</p>
              <p className="text-lg font-bold text-[#005f6b]">{intake.customer_name || "Not provided"}</p>
            </div>
            {intake.intake_type == 'appointment' && (
              <div>
                <p className="text-xs text-gray-500 uppercase">Event Type</p>
                <p className="text-lg font-bold text-[#d4af37]">{intake.event_type || "Not specified"}</p>
              </div>
            )}
            {intake.intake_type == 'service' && (
              <div>
                <p className="text-xs text-gray-500 uppercase">Urgency Level</p>
                <p className="text-lg font-bold text-[#d4af37]">{intake.service_urgency || "Not specified"}</p>
              </div>
            )}

            {intake.intake_type == 'appointment' && (
              <>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Event Date</p>
                  <p className="text-sm font-medium">{formatDate(intake.event_date) || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Appointment Time</p>
                  <p className="text-sm font-medium">{formatDate(intake.appointment_date) || "Not specified"}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Snapshot */}
        {intake.intake_type == 'appointment' && (
          <div className="mb-6 bg-[#00ced1]/10 border-l-4 border-[#00ced1] p-4">
            <h2 className="text-lg font-bold text-[#005f6b] mb-3">📸 Quick Snapshot</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-semibold">Venue:</span> {intake.venue || "Not specified"}
              </div>
              <div>
                <span className="font-semibold">Activity:</span> {parsed.activity || "Not specified"}
              </div>
            </div>
          </div>
        )}


        {parsed.sections && parsed.sections.map((sec) => (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              {sec.title}
            </h2>
            <div className="text-sm whitespace-pre-line">{sec.value}</div>
          </div>
        ))}

        {/* Size & Fit */}
        {parsed.sizeAndFit && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Size & Fit
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.sizeAndFit}</div>
          </div>
        )}

        {/* Coverage Preferences */}
        {parsed.coveragePreferences && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Coverage & Preferences
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.coveragePreferences}</div>
          </div>
        )}

        {/* Style & Aesthetic */}
        {parsed.styleAesthetic && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Style Direction
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.styleAesthetic}</div>
          </div>
        )}

        {/* Color Palette */}
        {parsed.colorPalette && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Color Palette
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.colorPalette}</div>
          </div>
        )}

        {/* Fabrics & Textures */}
        {parsed.fabricsTextures && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Fabrics & Textures
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.fabricsTextures}</div>
          </div>
        )}

        {/* Shoes */}
        {parsed.shoes && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2 pb-2 border-b border-gray-200">
              Shoes
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.shoes}</div>
          </div>
        )}

        {/* Absolutely Nots */}
        {parsed.absolutelyNots && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
            <h2 className="text-lg font-bold text-red-700 mb-2">
              ⚠️ Absolutely Nots
            </h2>
            <div className="text-sm whitespace-pre-line text-red-900">{parsed.absolutelyNots}</div>
          </div>
        )}

        {/* Special Notes */}
        {parsed.specialNotes && (
          <div className="mb-6 bg-[#d4af37]/10 border-l-4 border-[#d4af37] p-4">
            <h2 className="text-lg font-bold text-[#005f6b] mb-2">
              📝 Special Notes
            </h2>
            <div className="text-sm whitespace-pre-line">{parsed.specialNotes}</div>
          </div>
        )}

        {/* Chat Transcript - Collapsible */}
        {intake.chat_history && intake.chat_history.length > 0 && (
          <div className="mt-8 border-t-2 border-gray-200 pt-6 print:hidden">
            <button
              onClick={() => setShowTranscript(!showTranscript)}
              className="flex items-center gap-2 text-[#005f6b] font-semibold hover:text-[#00ced1] transition-colors"
            >
              {showTranscript ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              View Full Client Conversation
            </button>

            {showTranscript && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                {intake.chat_history.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`${msg.role === "user" ? "text-right" : "text-left"}`}
                  >
                    <div
                      className={`inline-block max-w-[80%] rounded-lg px-4 py-2 text-sm ${msg.role === "user"
                        ? "bg-[#00ced1] text-white"
                        : "bg-white text-gray-900 border border-gray-200"
                        }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceSheetView;
